import { useEffect, useRef, useState } from "react";
import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from "recharts";
import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  type ChartConfig,
} from "@/components/ui/chart";
import { formatChartDay, formatMoney } from "./ai-usage-format";

export interface StackedBarDay {
  /** `Y-m-d` */
  date: string;
  /** One cost per series, by index into `seriesLabels`. */
  values: number[];
  unpriced: number;
}

interface StackedBarChartProps {
  seriesLabels: string[];
  days: StackedBarDay[];
}

type ChartRow = Record<string, number | string>;

const seriesKey = (i: number): string => `m${i}`;
const seriesColor = (i: number): string => `hsl(var(--chart-${(i % 5) + 1}))`;

interface TooltipPayloadItem {
  dataKey?: string | number;
  value?: number | string;
  payload?: ChartRow;
}

interface StackedBarTooltipProps {
  active?: boolean;
  payload?: TooltipPayloadItem[];
  seriesLabels: string[];
}

export function StackedBarTooltip({ active, payload, seriesLabels }: StackedBarTooltipProps) {
  if (!active || !payload?.length) return null;
  const row = payload[0].payload;
  const unpriced = Number(row?.unpriced ?? 0);
  return (
    <div className="rounded-lg border border-border bg-background px-2.5 py-1.5 text-xs shadow-xl space-y-1">
      <div className="font-medium text-foreground">{row ? formatChartDay(String(row.date)) : null}</div>
      {payload.map((item) => {
        const index = Number(String(item.dataKey).slice(1));
        return (
          <div key={String(item.dataKey)} className="flex items-center justify-between gap-4">
            <span className="flex items-center gap-1.5 text-muted-foreground">
              <span
                className="h-2 w-2 shrink-0 rounded-[2px]"
                style={{ backgroundColor: seriesColor(index) }}
              />
              {seriesLabels[index]}
            </span>
            <span className="font-mono tabular-nums text-foreground">{formatMoney(Number(item.value ?? 0))}</span>
          </div>
        );
      })}
      {unpriced > 0 ? <div className="text-muted-foreground">{unpriced} unpriced call(s)</div> : null}
    </div>
  );
}

/** Estimated width of a "Sep 08" label at fontSize 11, plus the gap between neighbours. */
const TICK_SLOT_PX = 48;
/** Y axis, left margin and right margin: what the plot loses from the container width. */
const PLOT_INSET_PX = 100;
const TILTED_AXIS_HEIGHT = 56;

export interface XTickLayout {
  angle: number;
  textAnchor: "middle" | "end";
  height?: number;
}

/** Horizontal while every label fits (or the width is not known yet); tilted -45deg otherwise. */
export function xTickLayout(containerWidth: number, tickCount: number): XTickLayout {
  const plotWidth = containerWidth - PLOT_INSET_PX;
  const fits = containerWidth <= 0 || tickCount * TICK_SLOT_PX <= plotWidth;
  return fits
    ? { angle: 0, textAnchor: "middle" }
    : { angle: -45, textAnchor: "end", height: TILTED_AXIS_HEIGHT };
}

function useElementWidth<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    setWidth(el.getBoundingClientRect().width);
    const observer = new ResizeObserver((entries) => {
      const next = entries[0]?.contentRect.width;
      if (typeof next === "number") setWidth(next);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return [ref, width] as const;
}

export function StackedBarChart({ seriesLabels, days }: StackedBarChartProps) {
  const [wrapperRef, width] = useElementWidth<HTMLDivElement>();
  const tickLayout = xTickLayout(width, days.length);
  const config: ChartConfig = {};
  seriesLabels.forEach((label, i) => {
    config[seriesKey(i)] = { label, color: seriesColor(i) };
  });

  const data: ChartRow[] = days.map((day) => {
    const row: ChartRow = { date: day.date, unpriced: day.unpriced };
    seriesLabels.forEach((_, i) => {
      row[seriesKey(i)] = day.values[i] ?? 0;
    });
    return row;
  });

  return (
    <div ref={wrapperRef}>
    <ChartContainer config={config} className="aspect-auto h-[320px]">
      <BarChart data={data} margin={{ top: 8, right: 16, left: 24, bottom: 8 }}>
        <CartesianGrid vertical={false} stroke="hsl(var(--border))" />
        <XAxis
          dataKey="date"
          tickFormatter={formatChartDay}
          interval={0}
          angle={tickLayout.angle}
          textAnchor={tickLayout.textAnchor}
          height={tickLayout.height}
          fontSize={11}
          tickLine={false}
          axisLine={false}
        />
        <YAxis
          fontSize={11}
          tickLine={false}
          axisLine={false}
          tickFormatter={formatMoney}
          label={{
            value: "Cost (USD)",
            angle: -90,
            position: "insideLeft",
            offset: -16,
            style: { textAnchor: "middle", fontSize: 11, fontWeight: 600 },
          }}
        />
        <ChartTooltip content={<StackedBarTooltip seriesLabels={seriesLabels} />} />
        <ChartLegend content={<ChartLegendContent />} />
        {seriesLabels.map((_, i) => (
          <Bar
            key={seriesKey(i)}
            dataKey={seriesKey(i)}
            stackId="cost"
            fill={seriesColor(i)}
            radius={i === seriesLabels.length - 1 ? [4, 4, 0, 0] : [0, 0, 0, 0]}
          />
        ))}
      </BarChart>
    </ChartContainer>
    </div>
  );
}
